import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useGlossaryFeatureFlag } from 'src/hooks/useGlossaryFeatureFlag';
import {
    GetGlossaryResponse,
    GlossaryTerm,
    GlossaryTermMap,
} from 'src/types/glossary';
import { createAxiosInstance } from 'src/utils/axiosInstance';

/** Client-side timeout for GET /glossary. Requirement 5.4. */
const GLOSSARY_REQUEST_TIMEOUT_MS = 2_000;

export interface GlossaryContextType {
    /** `term.toLowerCase()` -> parsed entry. Empty when the feature is off. */
    terms: GlossaryTermMap;
    isLoading: boolean;
    error: Error | null;
    retrievedAt: string | null;
    isGlossaryEnabled: boolean;
}

const EMPTY_TERMS: GlossaryTermMap = new Map();

export const GlossaryContext = React.createContext<GlossaryContextType>({
    terms: EMPTY_TERMS,
    isLoading: false,
    error: null,
    retrievedAt: null,
    isGlossaryEnabled: false,
});

export const useGlossary = (): GlossaryContextType =>
    useContext(GlossaryContext);

interface GlossaryProviderProps {
    children: React.ReactNode;
}

/**
 * Fetches the glossary once on mount when the feature flag is enabled.
 *
 * On any failure (timeout, non-2xx, parse error) the provider sets `error`
 * and leaves `terms` empty — the frontend renders topic pages without
 * highlighting and does NOT retry within the current page view (Requirement
 * 5.6, 5.8).
 *
 * The axios instance already has auth, retries on 5xx (idempotent GET), and
 * the circuit breaker. We use it here with an AbortController-backed 2s
 * client-side timeout per Requirement 5.4; on timeout we treat the request
 * as a failure and fall through to "no highlights".
 */
export const GlossaryProvider = ({ children }: GlossaryProviderProps) => {
    const isGlossaryEnabled = useGlossaryFeatureFlag();
    const [terms, setTerms] = useState<GlossaryTermMap>(EMPTY_TERMS);
    const [retrievedAt, setRetrievedAt] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);

    // Dedicated axios instance so the /glossary call never shares state
    // (interceptor queues, canceled-token flags) with the main api
    // instance. The retry interceptor honors the config.signal both
    // before issuing a retry and during its backoff sleep — see
    // src/utils/axiosInstance.ts, abortableSleep + the aborted checks —
    // so controller.abort() from this 2 s timeout lands fast regardless
    // of whether an attempt is in-flight or we are in a backoff pause.
    // Lazily built on first render — `useRef(createAxiosInstance())`
    // would call the factory (and re-register auth + circuit-breaker
    // interceptors) on every render even though only the first result
    // is ever kept.
    const axiosRef = useRef<ReturnType<typeof createAxiosInstance>>();
    if (!axiosRef.current) {
        axiosRef.current = createAxiosInstance();
    }

    useEffect(() => {
        if (!isGlossaryEnabled) {
            return;
        }
        let cancelled = false;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            controller.abort();
        }, GLOSSARY_REQUEST_TIMEOUT_MS);

        setIsLoading(true);
        setError(null);

        axiosRef
            .current!.get<GetGlossaryResponse>('glossary', {
                signal: controller.signal,
            })
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const map: GlossaryTermMap = new Map();
                for (const entry of response.data.terms) {
                    if (
                        typeof entry?.term === 'string' &&
                        typeof entry?.definition === 'string' &&
                        entry.term.trim().length > 0
                    ) {
                        map.set(entry.term.toLowerCase(), {
                            term: entry.term,
                            definition: entry.definition,
                        });
                    }
                }
                setTerms(map);
                setRetrievedAt(response.data.retrievedAt ?? null);
            })
            .catch((err: unknown) => {
                if (cancelled) {
                    return;
                }
                // Any failure — timeout, non-2xx, parse — leaves terms empty.
                // No retry within the current page view (Requirement 5.6).
                const message =
                    err instanceof Error ? err.message : String(err);
                console.error(`Failed to load glossary: ${message}`);
                setError(err instanceof Error ? err : new Error(message));
                setTerms(EMPTY_TERMS);
            })
            .finally(() => {
                clearTimeout(timeoutId);
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
            controller.abort();
        };
    }, [isGlossaryEnabled]);

    const value = useMemo<GlossaryContextType>(
        () => ({
            terms,
            isLoading,
            error,
            retrievedAt,
            isGlossaryEnabled,
        }),
        [terms, isLoading, error, retrievedAt, isGlossaryEnabled]
    );

    return (
        <GlossaryContext.Provider value={value}>
            {children}
        </GlossaryContext.Provider>
    );
};
